import { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Plus, MoreHorizontal, Copy, Power, PowerOff, Trash2, RefreshCcw, Shuffle, Globe, AlertTriangle, CheckCircle2, UserCog, Upload, ImageOff, ShieldCheck, MessageSquareX } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/store/auth";
import { useEvents } from "@/store/events";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/ui/avatar";
import { AccountStatusBadge } from "@/components/AccountStatusBadge";
import { fmtDateTime, fmtRelative, initials, shortId } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AddAccountDialog } from "@/components/AddAccountDialog";
import type { Account, ProfileUpdate } from "@/lib/types";

function ProfileDialog({
  account,
  onClose,
  onSave,
  onPickAvatar,
  onDeleteAvatar,
  saving,
  avatarBusy,
}: {
  account: Account | null;
  onClose: () => void;
  onSave: (profile: ProfileUpdate) => void;
  onPickAvatar: (file: File) => void;
  onDeleteAvatar: () => void;
  saving: boolean;
  avatarBusy: boolean;
}) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [bio, setBio] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  // Resync на каждое открытие/обновление аккаунта (после WS-события профиля).
  useEffect(() => {
    setFirstName(account?.first_name ?? "");
    setLastName(account?.last_name ?? "");
    setUsername(account?.username ?? "");
    setBio(account?.bio ?? "");
  }, [account?.id, account?.first_name, account?.last_name, account?.username, account?.bio]);

  const displayName =
    [account?.first_name, account?.last_name].filter(Boolean).join(" ") || account?.phone || "";

  // Шлём только реально изменённые поля — лишние запросы к Telegram повышают риск бана.
  const save = () => {
    if (!account) return;
    const changed: ProfileUpdate = {};
    if (firstName !== (account.first_name ?? "")) changed.first_name = firstName;
    if (lastName !== (account.last_name ?? "")) changed.last_name = lastName;
    if (username !== (account.username ?? "")) changed.username = username;
    if (bio !== (account.bio ?? "")) changed.bio = bio;
    onSave(changed);
  };

  return (
    <Dialog open={!!account} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Профиль аккаунта</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Avatar
              name={displayName}
              initials={initials(account?.first_name, account?.last_name, account?.phone?.slice(-2))}
              size={64}
              src={account?.avatar_media_id ? api.mediaUrl(account.avatar_media_id) : null}
            />
            <div className="flex flex-col gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onPickAvatar(f);
                  e.target.value = "";
                }}
              />
              <Button variant="outline" size="sm" disabled={avatarBusy} onClick={() => fileRef.current?.click()}>
                <Upload className="h-4 w-4" /> Загрузить фото
              </Button>
              {account?.avatar_media_id && (
                <Button variant="ghost" size="sm" disabled={avatarBusy} onClick={onDeleteAvatar}>
                  <ImageOff className="h-4 w-4" /> Удалить фото
                </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Имя</Label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={64} />
            </div>
            <div className="space-y-1">
              <Label>Фамилия</Label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} maxLength={64} />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Username</Label>
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value.replace(/^@+/, ""))}
              placeholder="без @ · 5–32 символа"
              maxLength={32}
            />
          </div>
          <div className="space-y-1">
            <Label>О себе</Label>
            <Textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3} maxLength={70} />
            <p className="text-right text-[11px] text-muted-foreground">{bio.length}/70</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Отмена</Button>
          <Button onClick={save} disabled={saving}>
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export function Accounts() {
  const qc = useQueryClient();
  const { push } = useToast();
  const isAdmin = useAuth((s) => s.role) === "admin";
  const [adding, setAdding] = useState(false);
  const [profileAccountId, setProfileAccountId] = useState<string | null>(null);
  const [pickProxyAccountId, setPickProxyAccountId] = useState<string | null>(null);

  const { data: accounts = [], refetch, isFetching } = useQuery({
    queryKey: ["accounts"],
    queryFn: api.listAccounts,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["accounts"] });

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ["accounts"] });
    qc.invalidateQueries({ queryKey: ["proxies"] });
    qc.invalidateQueries({ queryKey: ["proxy-pool"] });
  };
  const enable = useMutation({ mutationFn: api.enableAccount, onSuccess: refreshAll });
  const disable = useMutation({ mutationFn: api.disableAccount, onSuccess: refreshAll });
  const del = useMutation({ mutationFn: api.deleteAccount, onSuccess: refreshAll });
  const reassign = useMutation({
    mutationFn: (id: string) => api.reassignProxy(id),
    onSuccess: () => {
      refreshAll();
      push({ title: "Прокси перевыдан", variant: "success" });
    },
  });
  const reassignToProxy = useMutation({
    mutationFn: ({ id, proxyId }: { id: string; proxyId: string }) => api.reassignProxy(id, proxyId),
    onSuccess: () => {
      refreshAll();
      push({ title: "Прокси назначен", variant: "success" });
    },
    onError: (e) => handleError(e),
  });
  const updateProfile = useMutation({
    mutationFn: ({ id, ...body }: { id: string } & ProfileUpdate) => api.updateProfile(id, body),
    onSuccess: () => {
      setProfileAccountId(null);
      push({ title: "Изменения профиля отправлены", variant: "success" });
    },
    onError: (e) => handleError(e),
  });
  const setAvatar = useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const m = await api.uploadMedia(file);
      return api.setAvatar(id, m.id);
    },
    onSuccess: () => push({ title: "Фото профиля отправлено", variant: "success" }),
    onError: (e) => handleError(e),
  });
  const deleteAvatar = useMutation({
    mutationFn: (id: string) => api.deleteAvatar(id),
    onSuccess: () => push({ title: "Запрос на удаление фото отправлен", variant: "success" }),
    onError: (e) => handleError(e),
  });
  const lockdownPrivacy = useMutation({
    mutationFn: (id: string) => api.lockdownPrivacy(id),
    onSuccess: () => push({ title: "Приватность: запрос отправлен", description: "Применяется аккаунтом…" }),
    onError: (e) => handleError(e),
  });
  const deleteAllChats = useMutation({
    mutationFn: (id: string) => api.deleteAllChats(id),
    onSuccess: () => push({ title: "Удаление всех чатов запущено", description: "Дождись подтверждения…" }),
    onError: (e) => handleError(e),
  });

  // Профиль применяется асинхронно воркером — слушаем WS, чтобы подтянуть
  // свежие имя/username/аватар и показать ошибку, если Telegram отклонил.
  useEffect(() => {
    return useEvents.getState().subscribe("*", (e) => {
      if (typeof e.type === "string" && e.type.startsWith("profile.")) {
        qc.invalidateQueries({ queryKey: ["accounts"] });
        if (e.type === "profile.update_failed") {
          push({
            title: "Telegram отклонил изменения",
            description: String((e.data as { detail?: string })?.detail ?? ""),
            variant: "destructive",
          });
        }
      }
      if (e.type === "privacy.locked_down") {
        push({
          title: "Приватность применена",
          description: "Номер, онлайн и звонки скрыты от всех",
          variant: "success",
        });
      }
      if (e.type === "chats.purged") {
        const d = e.data as { deleted?: number; remaining?: number; ok?: boolean };
        push(
          d.ok
            ? {
                title: "Все чаты удалены",
                description: `Удалено: ${d.deleted ?? 0}. Список в Telegram пуст.`,
                variant: "success",
              }
            : {
                title: "Удалены не все чаты",
                description: `Удалено: ${d.deleted ?? 0}, осталось: ${d.remaining ?? 0}. Попробуй ещё раз.`,
                variant: "destructive",
              }
        );
      }
    });
  }, [qc, push]);

  // Берём актуальную версию из списка — диалог обновится после WS-инвалидации.
  const profileAccount = accounts.find((a) => a.id === profileAccountId) ?? null;

  function copy(text: string, label = "ID") {
    navigator.clipboard.writeText(text);
    push({ title: `${label} скопирован`, description: text });
  }

  function handleError(e: unknown) {
    const msg = e instanceof ApiError ? e.message : String(e);
    push({ title: "Ошибка", description: msg, variant: "destructive" });
  }

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <div>
          <h1 className="text-lg font-semibold">Аккаунты</h1>
          <p className="text-sm text-muted-foreground">
            {accounts.length} {accounts.length === 1 ? "аккаунт" : "аккаунтов"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCcw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            Обновить
          </Button>
          {isAdmin && (
            <Button size="sm" onClick={() => setAdding(true)}>
              <Plus className="h-4 w-4" />
              Добавить аккаунт
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-background/95 backdrop-blur">
            <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-2 text-left font-medium">Аккаунт</th>
              <th className="px-4 py-2 text-left font-medium">Телефон</th>
              <th className="px-4 py-2 text-left font-medium">Статус</th>
              <th className="px-4 py-2 text-left font-medium">Last seen</th>
              <th className="px-4 py-2 text-left font-medium">Proxy</th>
              <th className="w-12 px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {accounts.map((a: Account) => {
              const name = a.username
                ? `@${a.username}`
                : [a.first_name, a.last_name].filter(Boolean).join(" ") || a.phone;
              return (
                <tr key={a.id} className="border-b border-border/50 transition-colors hover:bg-accent/40">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar name={name} initials={initials(a.first_name, a.last_name, a.phone.slice(-2))} src={a.avatar_media_id ? api.mediaUrl(a.avatar_media_id) : null} />
                      <div>
                        <div className="font-medium">{name}</div>
                        <button
                          onClick={() => copy(a.id, "account_id")}
                          className="font-mono text-xs text-muted-foreground hover:text-foreground"
                          title={a.id}
                        >
                          {shortId(a.id)} <Copy className="inline h-3 w-3 opacity-50" />
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{a.phone}</td>
                  <td className="px-4 py-3">
                    <AccountStatusBadge status={a.status} />
                    {a.last_error && (
                      <div
                        className="mt-1 max-w-[280px] truncate text-[11px] text-destructive/90"
                        title={a.last_error}
                      >
                        {a.last_error}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground" title={fmtDateTime(a.last_seen_at)}>
                    {a.last_seen_at ? fmtRelative(a.last_seen_at) : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {a.proxy ? (
                      <div className="flex items-center gap-1.5">
                        {a.proxy.is_healthy ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--success))]" />
                        ) : (
                          <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                        )}
                        <span className="font-mono text-muted-foreground" title={`${a.proxy.host}:${a.proxy.port}`}>
                          {a.proxy.label || `${a.proxy.host}:${a.proxy.port}`}
                        </span>
                      </div>
                    ) : (
                      <span className="text-destructive/80">нет прокси</span>
                    )}
                  </td>
                  <td className="px-2 py-3">
                    {isAdmin && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuLabel>Действия</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => enable.mutate(a.id, { onError: handleError })}>
                          <Power className="h-4 w-4" /> Активировать
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => disable.mutate(a.id, { onError: handleError })}>
                          <PowerOff className="h-4 w-4" /> Отключить
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => reassign.mutate(a.id, { onError: handleError })}>
                          <Shuffle className="h-4 w-4" /> Перевыдать прокси
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={(e) => {
                          e.preventDefault();
                          setTimeout(() => { setPickProxyAccountId(a.id); }, 0);
                        }}>
                          <Globe className="h-4 w-4" /> Выбрать прокси…
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={(e) => {
                          e.preventDefault();
                          setTimeout(() => { setProfileAccountId(a.id); }, 0);
                        }}>
                          <UserCog className="h-4 w-4" /> Профиль
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => lockdownPrivacy.mutate(a.id, { onError: handleError })}>
                          <ShieldCheck className="h-4 w-4" /> Скрыть номер/онлайн/звонки
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          destructive
                          onClick={() => {
                            if (confirm(`Удалить ВСЕ чаты аккаунта ${name}? Действие необратимо.`))
                              deleteAllChats.mutate(a.id, { onError: handleError });
                          }}
                        >
                          <MessageSquareX className="h-4 w-4" /> Удалить все чаты
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          destructive
                          onClick={() => {
                            if (confirm(`Удалить аккаунт ${name}?`)) del.mutate(a.id, { onError: handleError });
                          }}
                        >
                          <Trash2 className="h-4 w-4" /> Удалить
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    )}
                  </td>
                </tr>
              );
            })}
            {accounts.length === 0 && !isFetching && (
              <tr>
                <td colSpan={7} className="px-4 py-16 text-center text-sm text-muted-foreground">
                  Пока ни одного аккаунта. Нажми <strong>Добавить аккаунт</strong>.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AddAccountDialog open={adding} onOpenChange={setAdding} onDone={invalidate} />

      <ProfileDialog
        account={profileAccount}
        onClose={() => setProfileAccountId(null)}
        onSave={(p) => {
          if (!profileAccount) return;
          if (Object.keys(p).length === 0) {
            setProfileAccountId(null);
            return;
          }
          updateProfile.mutate({ id: profileAccount.id, ...p });
        }}
        onPickAvatar={(file) =>
          profileAccount && setAvatar.mutate({ id: profileAccount.id, file })
        }
        onDeleteAvatar={() => profileAccount && deleteAvatar.mutate(profileAccount.id)}
        saving={updateProfile.isPending}
        avatarBusy={setAvatar.isPending || deleteAvatar.isPending}
      />

      <PickProxyDialog
        accountId={pickProxyAccountId}
        onClose={() => setPickProxyAccountId(null)}
        onPicked={(proxyId) => {
          if (pickProxyAccountId) reassignToProxy.mutate({ id: pickProxyAccountId, proxyId });
        }}
      />
    </div>
  );
}

function PickProxyDialog({
  accountId,
  onClose,
  onPicked,
}: {
  accountId: string | null;
  onClose: () => void;
  onPicked: (proxyId: string) => void;
}) {
  const { data: proxies = [] } = useQuery({
    queryKey: ["proxies"],
    queryFn: api.listProxies,
    enabled: !!accountId,
  });
  const [proxyId, setProxyId] = useState("");

  useEffect(() => { setProxyId(""); }, [accountId]);

  return (
    <Dialog open={!!accountId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Выбрать прокси</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Select value={proxyId} onValueChange={setProxyId}>
            <SelectTrigger><SelectValue placeholder="Прокси…" /></SelectTrigger>
            <SelectContent>
              {proxies.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.label || `${p.host}:${p.port}`}
                  {p.in_use_by_account_ids.length > 0 ? ` · занят (${p.in_use_by_account_ids.length})` : ""}
                  {!p.is_healthy ? " · unhealthy" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Можно выбрать уже занятый прокси — он посадится на этот аккаунт в дополнение к остальным.
          </p>
          <Button
            className="w-full"
            disabled={!proxyId}
            onClick={() => { onPicked(proxyId); onClose(); }}
          >
            Назначить
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
